import {RuntimeAdapter} from '@assistant-ui/core/react';
import {useAui} from '@assistant-ui/react';
import {AuiProvider} from '@assistant-ui/store';
import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import React, {useCallback, useLayoutEffect, useMemo} from 'react';

import {billingStore} from '../../agentChat/billing';
import {
  type AgentChatAvailability,
  wbAgentControllerStore,
} from '../../agentChat/controller';
import {WBAgentRuntime} from '../../agentChat/runtime/backend';
import {
  AgentChatStoreProvider,
  selectActiveThread,
  useAgentChatStore,
} from '../../agentChat/store';
import {useAgentChatRuntime} from '../../agentChat/useAgentChatRuntime';
import {useThreadReadTracker} from '../../agentChat/useThreadReadTracker';
import {useNavContext} from '../NavContextProvider';
import {WBAgentControllerBridge} from '../WBAgent/WBAgentControllerBridge';
import {useComputeWBAgentBilling} from './billing/useComputeWBAgentBilling';
import {useRefreshBillingOnOpen} from './billing/useRefreshBillingOnOpen';
import {
  AgentChatAvailabilityContext,
  ShareTracesPolicyContext,
  useAgentChatAvailability,
  useComputeAgentChatAvailability,
  useComputeShareTracesPolicy,
} from './useAgentChatAvailability';

/**
 * AgentChatProvider supplies the chat store and availability to its subtree. The
 * AUI runtime is mounted separately, only when available, by
 * `AgentChatRuntimeBoundary`.
 */
export function AgentChatProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const availability = useComputeAgentChatAvailability();
  const shareTracesPolicy = useComputeShareTracesPolicy();

  usePublishAgentChatAvailability(availability);
  usePublishWBAgentBilling(availability.isAvailable);

  return (
    <AgentChatAvailabilityContext.Provider value={availability}>
      <ShareTracesPolicyContext.Provider value={shareTracesPolicy}>
        <AgentChatStoreProvider>{children}</AgentChatStoreProvider>
      </ShareTracesPolicyContext.Provider>
    </AgentChatAvailabilityContext.Provider>
  );
}

/**
 * AgentChatRuntimeBoundary mounts the AUI runtime and renders its children only
 * when the feature is available. Components that call `useAui` must render inside
 * it. Must itself render inside `AgentChatProvider`.
 */
export function AgentChatRuntimeBoundary({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element | null {
  const {isAvailable} = useAgentChatAvailability();
  if (!isAvailable) {
    return null;
  }

  return <AssistantRuntime>{children}</AssistantRuntime>;
}

function AssistantRuntime({children}: {children: React.ReactNode}) {
  const {params} = useNavContext();
  const wbAgentClient = useWbAgentClient(params.entityName, params.projectName);
  const store = useAgentChatStore();

  const getParentNodeId = useCallback(
    (nodeId: string) => {
      const thread = selectActiveThread(store.getState());
      return thread?.messageTree.parentId[nodeId] ?? null;
    },
    [store]
  );

  const agentRuntime = useMemo(
    () =>
      wbAgentClient ? new WBAgentRuntime(wbAgentClient, getParentNodeId) : null,
    [wbAgentClient, getParentNodeId]
  );

  const auiRuntime = useAgentChatRuntime(agentRuntime, params);
  useThreadReadTracker(store);
  useRefreshBillingOnOpen();
  const aui = useAui({
    threads: RuntimeAdapter(auiRuntime),
  });

  return (
    <AuiProvider value={aui}>
      <WBAgentControllerBridge />
      {children}
    </AuiProvider>
  );
}

/**
 * usePublishAgentChatAvailability publishes the computed availability into the
 * controller store, so buttons anywhere in the app can read it; the value is
 * otherwise local to this subtree.
 */
function usePublishAgentChatAvailability(
  availability: AgentChatAvailability
): void {
  // The layout effect runs before paint, so external buttons don't show a
  // stale-enabled frame after the feature turns off.
  useLayoutEffect(() => {
    wbAgentControllerStore.setAvailability(availability);
  }, [availability]);

  // Reset on unmount so a torn-down provider doesn't leave a stuck 'available'
  // with a dead handle.
  useLayoutEffect(
    () => () => {
      wbAgentControllerStore.setAvailability({
        isAvailable: false,
        unavailableReason: 'loading',
      });
    },
    []
  );
}

// Whether WB Agent billing enforcement is live. While false, the store is fed
// `unenforced` so every gate stays open and the enforcement machinery ships
// inert.
const WB_AGENT_BILLING_ENABLED: boolean = false;

/**
 * usePublishWBAgentBilling derives the WB Agent billing snapshot and publishes
 * it into the module-singleton store, so consumers outside this subtree can
 * gate on access.
 */
function usePublishWBAgentBilling(enabled: boolean): void {
  const {billing, refresh} = useComputeWBAgentBilling(enabled);

  // The layout effects run before paint so an out-of-tree gate never shows a
  // stale frame.
  useLayoutEffect(() => {
    billingStore.setBilling(
      WB_AGENT_BILLING_ENABLED ? billing : {status: 'unenforced'}
    );
  }, [billing]);

  useLayoutEffect(() => billingStore.registerRefresh(refresh), [refresh]);

  // Reset on unmount so a torn-down provider doesn't leave stale billing.
  useLayoutEffect(() => () => billingStore.reset(), []);
}
